import { useMemo, useState } from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { movieList } from '@/mocks/movies';
import PageHero from '@/components/feature/PageHero';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import MovieCard from './components/MovieCard';

const PAGE_SIZE = 8;

const typeOptions = ['全部', '电影', '电视剧', '综艺'];
const eraOptions = ['全部', '近一年', '近三年'];
const sortOptions = [
  { key: 'default', label: '默认排序' },
  { key: 'ratingDesc', label: '评分从高到低' },
  { key: 'ratingAsc', label: '评分从低到高' },
];

export default function MoviesPage() {
  usePageMeta({
    title: '影视推荐 - 靠逼',
    description: '靠逼影视推荐频道精选电影、电视剧与综艺佳作，按类型、年代与评分筛选，帮你快速找到值得一看的作品。',
    keywords: '影视推荐,电影,电视剧,综艺,靠逼',
  });

  const [type, setType] = useState('全部');
  const [era, setEra] = useState('全部');
  const [sort, setSort] = useState('default');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = [...movieList];
    if (type !== '全部') list = list.filter((m) => m.type === type);
    if (era === '近一年') list = list.filter((m) => m.year >= 2026);
    if (era === '近三年') list = list.filter((m) => m.year >= 2024);
    if (sort === 'ratingDesc') list.sort((a, b) => b.rating - a.rating);
    if (sort === 'ratingAsc') list.sort((a, b) => a.rating - b.rating);
    return list;
  }, [type, era, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setType('全部');
    setEra('全部');
    setSort('default');
    setPage(1);
  };

  const change = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
      active
        ? 'bg-primary-500 font-medium text-background-50'
        : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
    }`;

  return (
    <div className="w-full">
      <PageHero
        title="影视推荐"
        subtitle="精选电影、电视剧与综艺，按类型、年代与评分轻松筛选"
        crumbs={[{ label: '影视推荐' }]}
        icon="ri-film-line"
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-foreground-700">类型</span>
              {typeOptions.map((t) => (
                <button key={t} type="button" className={chipClass(type === t)} onClick={() => change(() => setType(t))}>
                  {t}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-foreground-700">年代</span>
              {eraOptions.map((e) => (
                <button key={e} type="button" className={chipClass(era === e)} onClick={() => change(() => setEra(e))}>
                  {e}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 md:ml-auto">
              <span className="text-sm font-medium text-foreground-700">排序</span>
              <select
                value={sort}
                onChange={(e) => change(() => setSort(e.target.value))}
                className="rounded-md border border-background-200 bg-background-100 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
              >
                {sortOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-foreground-800">{filtered.length}</span> 部作品
          </p>
          {(type !== '全部' || era !== '全部' || sort !== 'default') && (
            <button
              type="button"
              onClick={reset}
              className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        {current.length === 0 ? (
          <div className="mt-4">
            <EmptyState
              icon="ri-film-line"
              title="没有符合当前筛选的影视内容"
              description="试试调整类型、年代或排序条件，或者一键重置筛选。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {current.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        )}

        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}